import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { Download, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';

import { formatDateTimeMediumDotted } from '@klareospace/pharos-lib-front-utils';
import { getFullName } from '@klareospace/pharos-lib-utils';
import type { ImportHistoryItem } from '@klareospace/pharos-lib-types';
import type { DataTableColumn, RowAction, SimplePagination } from '@klareospace/pharos-lib-web-utils';
import {
  createActionsColumn,
  DataTable,
  ImportStatusBadge,
  RowActionsMenu,
  TextCell,
  useDataTablePagination,
} from '@klareospace/pharos-lib-web-utils';

import { computeSuccessRate, countRejected, resolveImportTypeLabel } from '../utils';
import { useDownloadErrorReport, useDownloadImportFile } from '../hooks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ImportHistoryTableProps {
  imports: ImportHistoryItem[];
  isLoading: boolean;
  pagination: SimplePagination;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Its row menu exposes two contextual actions: "View import report" navigates to the report page,
 * and "Download error report" is disabled when the row has no rejected line. There is deliberately
 * no "Cancel import" action.
 */
export function ImportHistoryTable({ imports, isLoading, pagination }: ImportHistoryTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t, i18n } = useTranslation('imports');
  const navigate = useNavigate();
  const { mutate: downloadImportFile } = useDownloadImportFile();
  const { mutate: downloadErrorReport } = useDownloadErrorReport();

  const handleDownload = useCallback(
    (importDoc: ImportHistoryItem): void => {
      downloadImportFile(importDoc);
    },
    [downloadImportFile],
  );

  const getRowActions = useCallback(
    (importDoc: ImportHistoryItem): RowAction[] => [
      {
        icon: <FileText size={16} />,
        label: t('history.actions.viewReport'),
        onClick: (): void => {
          void navigate({ params: { importId: importDoc.id }, to: '/imports/history/$importId' });
        },
      },
      {
        disabled: countRejected(importDoc) === 0,
        icon: <Download size={16} />,
        label: t('history.actions.downloadErrorReport'),
        onClick: (): void => {
          downloadErrorReport(importDoc);
        },
      },
    ],
    [downloadErrorReport, navigate, t],
  );

  const columns = useMemo<DataTableColumn<ImportHistoryItem>[]>(
    () => [
      {
        cell: (importDoc): React.ReactNode => (
          <button
            className="truncate text-sm font-medium text-primary hover:underline"
            onClick={(): void => handleDownload(importDoc)}
            type="button"
          >
            {importDoc.fileName}
          </button>
        ),
        header: t('history.columns.file'),
        key: 'file',
      },
      {
        cell: (importDoc): React.ReactNode => <TextCell>{resolveImportTypeLabel(t, importDoc.kind)}</TextCell>,
        header: t('common.importTypeLabel'),
        key: 'importType',
      },
      {
        cell: (importDoc): React.ReactNode => (
          <TextCell truncate={false}>{formatDateTimeMediumDotted(importDoc.createdAt, i18n.language)}</TextCell>
        ),
        header: t('history.columns.timestamp'),
        key: 'timestamp',
      },
      {
        // Defensive: a single legacy row shaped without `user` must degrade to a blank cell, not
        // throw and take down the whole table (`user` stays required in the `ImportHistoryItem` type).
        cell: (importDoc): React.ReactNode => (
          <TextCell>{getFullName(importDoc.user?.firstName, importDoc.user?.lastName)}</TextCell>
        ),
        header: tCommon('fields.user'),
        key: 'user',
      },
      {
        cell: (importDoc): React.ReactNode => importDoc.linesCount.total,
        header: t('history.columns.lines'),
        key: 'lines',
      },
      {
        cell: (importDoc): React.ReactNode => importDoc.linesCount.created,
        header: t('common.created'),
        key: 'created',
      },
      {
        cell: (importDoc): React.ReactNode => importDoc.linesCount.updated,
        header: t('common.updated'),
        key: 'updated',
      },
      {
        cell: (importDoc): React.ReactNode => countRejected(importDoc),
        header: t('history.columns.errors'),
        key: 'errors',
      },
      {
        cell: (importDoc): React.ReactNode => `${computeSuccessRate(importDoc)}%`,
        header: t('common.successRate'),
        key: 'successRate',
      },
      {
        cell: (importDoc): React.ReactNode => (
          <ImportStatusBadge label={t(`history.status.${importDoc.state}`)} state={importDoc.state} />
        ),
        header: tCommon('fields.status'),
        key: 'status',
      },
      createActionsColumn<ImportHistoryItem>((importDoc) => (
        <RowActionsMenu actions={getRowActions(importDoc)} ariaLabel={tCommon('table.actionsHeader')} />
      )),
    ],
    [getRowActions, handleDownload, i18n.language, t],
  );

  const paginationLabels = useMemo(
    () => ({
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current: number, total: number): string => tCommon('pagination.pageLabel', { current, total }),
      pageSizeLabel: tCommon('pagination.rowsPerPage'),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (start: number, end: number, total: number): string =>
        tCommon('pagination.showing', { end, start, total }),
    }),
    [tCommon],
  );

  const tablePagination = useDataTablePagination({ labels: paginationLabels, pagination });

  return (
    <DataTable<ImportHistoryItem>
      ariaLabel={tCommon('fields.importHistoryTitle')}
      columns={columns}
      emptyMessage={t('history.empty')}
      isLoading={isLoading}
      pagination={tablePagination}
      rowKey={(importDoc): string => importDoc.id}
      rows={imports}
    />
  );
}
